/**
 * Supplier Contract Review — output schema + catalog constants.
 *
 * Mirrors `output-schema.md` from the developer handoff package. The agent
 * returns `ContractReview`; the UI persists it as `ContractReviewRecord` under
 * /workflows/contract-review/projects/{id} on the default Firestore database.
 */

export type Severity = "red" | "yellow" | "green";
export type OverallRisk = "high" | "medium" | "low";
export type UserSide = "buyer" | "supplier";
export type ReviewBasis = "client_playbook" | "standard_positions";
export type RedlinePriority = "must-have" | "should-have" | "nice-to-have";

/** Free tier can generate this many contract reviews before upgrading. */
export const FREE_REVIEW_LIMIT = 1;

export interface Redline {
  current_language: string;
  proposed_language: string;
  rationale: string;
  priority: RedlinePriority | string;
  fallback: string;
}

export interface Clause {
  clause_id: string;
  clause_name: string;
  severity: Severity;
  contract_says: string;
  standard_position: string;
  business_impact: string;
  /** Empty / omitted for GREEN clauses — render no redline box. */
  redline?: Redline;
}

export interface KeyFinding {
  severity: Severity;
  clause_id: string;
  summary: string;
}

export interface NegotiationItem {
  priority: RedlinePriority | string;
  /** Clause / section negotiated, with source location where available. */
  issue: string;
  /** Concrete negotiation objective. */
  goal: string;
  /** Acceptable concession if the goal is rejected. */
  fallback: string;
}

export interface NegotiationStrategy {
  /** Structured negotiation rows (Priority | Issue | Goal | Fallback). */
  items?: NegotiationItem[];
  sequencing_note?: string;
  /** Legacy tier lists kept for reviews saved before the structured table. */
  tier_1_must_haves?: string[];
  tier_2_should_haves?: string[];
  tier_3_nice_to_haves?: string[];
}

/** The agent output (output-schema.md). */
export interface ContractReview {
  document_name: string;
  parties: string;
  contract_type: string;
  user_side: UserSide | string;
  deadline: string;
  review_basis: ReviewBasis | string;
  review_basis_note: string;
  overall_risk: OverallRisk | string;
  key_findings: KeyFinding[];
  clauses: Clause[];
  negotiation_strategy: NegotiationStrategy;
  next_steps: string[];
}

/** Persisted review — the schema plus workspace metadata. */
export interface ContractReviewRecord extends ContractReview {
  id: string;
  created_by: string;
  created_by_email: string;
  client_id: string;
  client_name: string;
  created_at: string;
  updated_at: string;
  source_text: string;
  source_name: string;
  /** Base64 of the original uploaded .docx (present for docx-intake reviews). */
  source_docx_b64?: string;
  /** Follow-up conversation with the review agent — persists with the review. */
  chat?: ChatMessage[];
}

/** A redline revision proposed by the follow-up agent for a specific clause. */
export interface RevisedRedlineProposal {
  clause_id: string;
  proposed_language: string;
  change_summary: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  proposed_redlines: RevisedRedlineProposal[];
  truncated?: boolean;
}

/** Feedback left by an external reviewer via the shared link. */
export interface ContractReviewFeedback {
  id: string;
  review_id: string;
  review_title: string;
  clause_id?: string | null;
  clause_name?: string | null;
  comment: string;
  /** Optional replacement / suggestion the reviewer typed for the section. */
  proposed_language?: string | null;
  /** "pending" | "accepted" | "declined" */
  status: string;
  decided_by?: string | null;
  decided_at?: string | null;
  applied_at?: string | null;
  created_by: string;
  created_by_email: string;
  created_at: string;
}

/** Input to POST /api/contracts/reviews/shared/{token}/feedback. */
export interface ReviewFeedbackInput {
  comment: string;
  clause_id?: string | null;
  clause_name?: string | null;
  proposed_language?: string | null;
}

/** Input to POST /api/contracts/reviews/feedback/{id}/decision. */
export interface FeedbackDecisionInput {
  decision: "accepted" | "declined";
  apply: boolean;
}

export type FeedbackStatus = "pending" | "accepted" | "declined";

/** Input to POST /api/contracts/reviews/generate. */
export interface ReviewRequest {
  contract_text: string;
  contract_type: string;
  user_side: UserSide;
  focus_areas: string;
  constraints: string;
  must_not_lose: string;
  client_id: string;
  client_name: string;
  deadline: string;
  deal_context: string;
}

export const CONTRACT_TYPES = [
  "Supply Agreement",
  "Manufacturing Agreement",
  "Distribution Agreement",
  "Master Services Agreement",
  "Tooling / Mold Agreement",
] as const;

export const FOCUS_CHIPS = [
  "MOQ flexibility",
  "Lead time / OTIF",
  "Price escalation cap",
  "Quality & defect liability",
  "Force majeure exposure",
  "IP & tooling ownership",
] as const;

export const CONSTRAINT_CHIPS = [
  "Deadline-driven",
  "Sole-source risk",
  "Regulatory requirement",
  "Long-standing relationship",
] as const;

/** True when a clause carries a usable redline. */
export function hasRedline(clause: Clause): boolean {
  return Boolean(clause.redline?.proposed_language?.trim());
}

/** Per-section negotiation posture, derived from severity + redline presence. */
export function negotiationPosture(clause: Clause): string {
  if (!hasRedline(clause)) return "Hold as-is — section already acceptable";
  if (clause.severity === "red") {
    return clause.redline?.priority === "must-have"
      ? "Must-win — open the negotiation here"
      : "High priority — firm floor, limited give";
  }
  if (clause.severity === "yellow") return "Improve — trade rather than block";
  return "Hold as-is — keep favorable language";
}

/** Tier label from the clause's redline priority (or "Hold"). */
export function negotiationPriority(clause: Clause): string {
  const p = clause.redline?.priority;
  if (p === "must-have") return "Tier 1 — Must-have";
  if (p === "should-have") return "Tier 2 — Should-have";
  if (p === "nice-to-have") return "Tier 3 — Nice-to-have";
  return "Hold";
}

/** What to ask for in the negotiation for this section. */
export function negotiationAsk(clause: Clause): string {
  if (hasRedline(clause) && clause.redline?.proposed_language) {
    return clause.redline.proposed_language;
  }
  return "No change requested — hold current language";
}

/** Concession / fallback position for the section. */
export function negotiationFallback(clause: Clause): string {
  if (hasRedline(clause) && clause.redline?.fallback) return clause.redline.fallback;
  return hasRedline(clause) ? "None specified — hold position" : "—";
}

/** Short MUST / SHOULD / NICE label for the summary table. */
export function negotiationPriorityLabel(priority: string): string {
  const v = (priority || "").toLowerCase();
  if (v === "must-have") return "MUST";
  if (v === "should-have") return "SHOULD";
  if (v === "nice-to-have") return "NICE";
  if (v === "hold") return "HOLD";
  return priority || "—";
}

export function severityCounts(clauses: Clause[]): { red: number; yellow: number; green: number } {
  return clauses.reduce(
    (acc, c) => {
      if (c.severity === "red") acc.red += 1;
      else if (c.severity === "yellow") acc.yellow += 1;
      else acc.green += 1;
      return acc;
    },
    { red: 0, yellow: 0, green: 0 },
  );
}

export function overallRiskLabel(risk: string): string {
  const r = (risk || "").toLowerCase();
  if (r === "high") return "High";
  if (r === "medium") return "Medium";
  if (r === "low") return "Low";
  return risk || "—";
}
